import { useTranslate } from "@similarweb/i18n-provider";
import type { ActivationAlertsContractSocialAdFeedGroup } from "@similarweb/pro-api-client";
import { useTracking } from "@similarweb/tracking-provider";
import { Badge, Carousel } from "@similarweb/ui-components";

import { AlertsGroupCarousel } from "../../../common/AlertsGroupCarousel";
import { CardLayout } from "../../../common/card-layout/CardLayout";
import { useSelectedGroupItemStore } from "../../../common/modal/useSelectedGroupItemStore";
import { SeeAllButton } from "../../../common/SeeAllButton";
import { SeeMoreCard } from "../../../common/SeeMoreCard";
import { adsTypeTranslationKeyMap } from "../../utils/translationKeysConfig";

import { socialAdCardSize, SocialAdCard } from "./SocialAdCard";

type SocialAdsAlertProps = {
    favicon: string;
    group: ActivationAlertsContractSocialAdFeedGroup;
    groupIndex: number;
};

export const SocialAdsAlert = ({ favicon, group, groupIndex }: SocialAdsAlertProps) => {
    const { translate } = useTranslate();
    const { track } = useTracking();
    const setSelectedGroupItem = useSelectedGroupItemStore((store) => store.setSelectedGroupItem);

    const hasMoreAds = group.totalCount > group.ads.length;

    const handleAdClick = (index: number) => {
        track("Alert Card", "click", "open alert/alerts/social-ads");
        setSelectedGroupItem({ itemIndex: index, groupIndex });
    };

    const handleSeeAllClick = () => {
        track("Alert Card", "click", "open alert/alerts/social-ads");
        setSelectedGroupItem({ itemIndex: 0, groupIndex });
    };

    const handleSeeMoreClick = () => {
        track("Alert Card", "click", "open alert/alerts/social-ads");
        setSelectedGroupItem({ itemIndex: group.ads.length, groupIndex });
    };

    return (
        <CardLayout
            iconUrl={favicon}
            onClick={handleSeeAllClick}
            data-automation="social-ads-alert"
            data-domain={group.domain}
        >
            <CardLayout.Header>
                <Badge data-automation="alert-badge">{translate(adsTypeTranslationKeyMap.Social)}</Badge>
                <CardLayout.Title>
                    {translate("activation.alerts.cards.social-ads.title", {
                        count: group.totalCount,
                        domain: group.domain,
                    })}
                </CardLayout.Title>
            </CardLayout.Header>
            <CardLayout.Content>
                <AlertsGroupCarousel>
                    {group.ads.map((ad, index) => (
                        <Carousel.Item key={ad.id}>
                            <SocialAdCard
                                ad={ad}
                                onClick={() => handleAdClick(index)}
                            />
                        </Carousel.Item>
                    ))}
                    {hasMoreAds ? (
                        <Carousel.Item key="see-more-card">
                            <SeeMoreCard
                                width={socialAdCardSize.width}
                                height={socialAdCardSize.height}
                                onClick={handleSeeMoreClick}
                                dataAutomation="social-ad-see-more-card"
                            />
                        </Carousel.Item>
                    ) : null}
                </AlertsGroupCarousel>
                <SeeAllButton
                    onClick={handleSeeAllClick}
                    isDisabled={!group.ads.length}
                    dataAutomation="see-all-ads-button"
                >
                    {translate("activation.alerts.cards.ads.see-all")}
                </SeeAllButton>
            </CardLayout.Content>
        </CardLayout>
    );
};
